import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';

interface Country { id: number; iso2: string; name: string; dial_code: string; }
interface URule { country_id: number; allowed: boolean; }
interface PUser { id: number; username: string; role: string; rules: URule[]; }
interface CP { tenant_id: number; countries: Country[]; tenant_allowed: number[]; users: PUser[]; }

const PIN: Record<string, number> = { AE: 0, GB: 1, US: 2 };
const flag = (iso: string) => iso.replace(/./g, (c) => String.fromCodePoint(127397 + c.charCodeAt(0)));

export default function DialingPolicyPage(_props: { user?: any }) {
  const [data, setData] = useState<CP | null>(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(''); const [msg, setMsg] = useState('');
  const [q, setQ] = useState('');
  const [tab, setTab] = useState<'tenant' | 'users'>('tenant');
  const [selUser, setSelUser] = useState<number | null>(null);
  const [savingT, setSavingT] = useState(false); const [savingU, setSavingU] = useState(false);
  const [tenantSel, setTenantSel] = useState<Set<number>>(new Set());
  const [userRules, setUserRules] = useState<Record<number, 'inherit' | 'allow' | 'deny'>>({});

  const load = useCallback(async () => {
    setLoading(true); setErr('');
    try { const r = await axios.get('/api/country-policy'); setData(r.data); setTenantSel(new Set(r.data.tenant_allowed)); }
    catch (e: any) { setErr(e?.response?.data?.error || 'Load failed'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    if (selUser == null || !data) return;
    const u = data.users.find((x) => x.id === selUser); const m: Record<number, 'inherit' | 'allow' | 'deny'> = {};
    (u?.rules || []).forEach((r) => { m[r.country_id] = r.allowed ? 'allow' : 'deny'; });
    setUserRules(m);
  }, [selUser, data]);

  const toggleTenant = (cid: number) => { const s = new Set(Array.from(tenantSel)); s.has(cid) ? s.delete(cid) : s.add(cid); setTenantSel(s); setMsg(''); };
  const saveTenant = async () => {
    setSavingT(true); setMsg(''); setErr('');
    try { await axios.put('/api/country-policy/tenant', { country_ids: Array.from(tenantSel) }); setMsg('Organisation countries saved.'); await load(); }
    catch (e: any) { setErr(e?.response?.data?.error || 'Save failed'); } finally { setSavingT(false); }
  };
  const setUState = (cid: number, st: 'inherit' | 'allow' | 'deny') => { setUserRules((p) => ({ ...p, [cid]: st })); setMsg(''); };
  const saveUser = async () => {
    if (selUser == null) return; setSavingU(true); setMsg(''); setErr('');
    const rules = Object.entries(userRules).filter(([, v]) => v !== 'inherit').map(([cid, v]) => ({ country_id: Number(cid), allowed: v === 'allow' }));
    try { await axios.put('/api/country-policy/user/' + selUser, { rules }); setMsg('User overrides saved.'); await load(); }
    catch (e: any) { setErr(e?.response?.data?.error || 'Save failed'); } finally { setSavingU(false); }
  };

  if (loading) return <div style={{ padding: 24 }}>Loading…</div>;
  if (!data) return <div style={{ padding: 24, color: '#b00' }}>{err || 'No data'}</div>;

  const sortC = (list: Country[]) => [...list].sort((a, b) => ((PIN[a.iso2] ?? 99) - (PIN[b.iso2] ?? 99)) || a.name.localeCompare(b.name));
  const match = (c: Country) => c.name.toLowerCase().includes(q.toLowerCase()) || c.dial_code.includes(q) || c.iso2.toLowerCase().includes(q.toLowerCase());
  const countries = sortC(data.countries.filter(match));
  const tenantAllowedSet = new Set(data.tenant_allowed);
  // Select / clear every country — only the ones matching the search when a search is typed
  const setAllTenant = (on: boolean) => { const s = new Set(Array.from(tenantSel)); countries.forEach((c) => (on ? s.add(c.id) : s.delete(c.id))); setTenantSel(s); setMsg(''); };

  const wrap: React.CSSProperties = { padding: 24, maxWidth: 1040 };
  const tabBtn = (a: boolean): React.CSSProperties => ({ padding: '8px 16px', border: 'none', borderBottom: a ? '3px solid #6366f1' : '3px solid transparent', background: 'none', fontWeight: 600, cursor: 'pointer', color: a ? '#6366f1' : '#555' });
  const chip = (on: boolean): React.CSSProperties => ({ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 12px', border: '1px solid ' + (on ? '#6366f1' : '#ddd'), borderRadius: 8, background: on ? '#eef0ff' : '#fff', cursor: 'pointer', fontSize: 14 });
  const btn: React.CSSProperties = { padding: '10px 18px', background: '#6366f1', color: '#fff', border: 'none', borderRadius: 8, fontWeight: 600, cursor: 'pointer' };
  const seg = (a: boolean, color: string): React.CSSProperties => ({ padding: '4px 10px', border: '1px solid ' + (a ? color : '#ddd'), background: a ? color : '#fff', color: a ? '#fff' : '#666', cursor: 'pointer', fontSize: 12, fontWeight: 600 });

  return (
    <div style={wrap}>
      <h2 style={{ margin: '0 0 4px' }}>Calling Countries</h2>
      <p style={{ color: '#666', marginTop: 0 }}>
        Choose which countries this organisation can call. A user's own rule (Allow / Deny) always overrides the organisation list.
        Countries not allowed here — and not overridden for the user — are blocked.
      </p>
      {err && <div style={{ background: '#fdecea', color: '#b00', padding: 10, borderRadius: 8, marginBottom: 10 }}>{err}</div>}
      {msg && <div style={{ background: '#e8f5e9', color: '#1b5e20', padding: 10, borderRadius: 8, marginBottom: 10 }}>{msg}</div>}

      <div style={{ borderBottom: '1px solid #eee', marginBottom: 16 }}>
        <button style={tabBtn(tab === 'tenant')} onClick={() => setTab('tenant')}>Organisation countries</button>
        <button style={tabBtn(tab === 'users')} onClick={() => setTab('users')}>Per-user overrides</button>
      </div>

      <input placeholder="Search country or dial code…" value={q} onChange={(e) => setQ(e.target.value)}
        style={{ width: '100%', maxWidth: 340, padding: 10, border: '1px solid #ddd', borderRadius: 8, marginBottom: 14 }} />

      {tab === 'tenant' && (
        <div>
          <div style={{ marginBottom: 12, color: '#666', display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
            <span>{tenantSel.size} of {data.countries.length} allowed</span>
            <button style={{ ...seg(false, '#6366f1'), borderRadius: 6 }} onClick={() => setAllTenant(true)}>{q ? `Select all shown (${countries.length})` : 'Select all'}</button>
            <button style={{ ...seg(false, '#6366f1'), borderRadius: 6 }} onClick={() => setAllTenant(false)}>{q ? `Clear all shown (${countries.length})` : 'Clear all'}</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(230px,1fr))', gap: 8 }}>
            {countries.map((c) => {
              const on = tenantSel.has(c.id);
              return (
                <div key={c.id} style={chip(on)} onClick={() => toggleTenant(c.id)}>
                  <input type="checkbox" checked={on} readOnly />
                  <span>{flag(c.iso2)} {c.name}</span>
                  <span style={{ marginLeft: 'auto', color: '#999' }}>+{c.dial_code}</span>
                </div>
              );
            })}
          </div>
          <div style={{ marginTop: 18 }}>
            <button style={{ ...btn, opacity: savingT ? 0.6 : 1 }} disabled={savingT} onClick={saveTenant}>{savingT ? 'Saving…' : 'Save organisation countries'}</button>
          </div>
        </div>
      )}

      {tab === 'users' && (
        <div style={{ display: 'flex', gap: 20 }}>
          <div style={{ minWidth: 200 }}>
            <div style={{ fontWeight: 600, marginBottom: 8 }}>Users</div>
            {data.users.map((u) => (
              <div key={u.id} onClick={() => setSelUser(u.id)}
                style={{ padding: '8px 10px', borderRadius: 8, cursor: 'pointer', background: selUser === u.id ? '#eef0ff' : 'transparent', fontWeight: selUser === u.id ? 600 : 400 }}>
                {u.username}{u.rules.length > 0 && <span style={{ color: '#6366f1', fontSize: 12 }}> • {u.rules.length}</span>}
              </div>
            ))}
          </div>
          <div style={{ flex: 1 }}>
            {selUser == null ? <div style={{ color: '#999', padding: 20 }}>Select a user to set per-country overrides.</div> : (
              <div>
                <p style={{ color: '#666', marginTop: 0 }}>“Inherit” follows the organisation list. “Allow” / “Deny” override it for this user only.</p>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(320px,1fr))', gap: 6 }}>
                  {countries.map((c) => {
                    const st = userRules[c.id] || 'inherit';
                    const tHint = tenantAllowedSet.has(c.id) ? 'org: allowed' : 'org: blocked';
                    return (
                      <div key={c.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
                        <span style={{ flex: 1 }}>{flag(c.iso2)} {c.name} <span style={{ color: '#bbb', fontSize: 11 }}>({tHint})</span></span>
                        <div style={{ display: 'flex' }}>
                          <button style={{ ...seg(st === 'inherit', '#888'), borderRadius: '6px 0 0 6px' }} onClick={() => setUState(c.id, 'inherit')}>Inherit</button>
                          <button style={seg(st === 'allow', '#2e7d32')} onClick={() => setUState(c.id, 'allow')}>Allow</button>
                          <button style={{ ...seg(st === 'deny', '#c62828'), borderRadius: '0 6px 6px 0' }} onClick={() => setUState(c.id, 'deny')}>Deny</button>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div style={{ marginTop: 18 }}>
                  <button style={{ ...btn, opacity: savingU ? 0.6 : 1 }} disabled={savingU} onClick={saveUser}>{savingU ? 'Saving…' : 'Save user overrides'}</button>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
